import React, { useState, useEffect } from 'react';
import { warrantyApi } from '../../services/api';

interface RmaRequest {
  id: string;
  rmaNumber: string;
  orderNumber?: string;
  component: string;
  issueType: string;
  description: string;
  status: string;
  adminNotes?: string;
  denialReason?: string;
  photoUrls: string[];
  dealerCompanyName?: string;
  warrantyCustomerName?: string;
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string;
}

const statusColors: Record<string, string> = {
  pending_review: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  denied: 'bg-red-100 text-red-800',
  replacement_shipped: 'bg-blue-100 text-blue-800',
  resolved: 'bg-gray-100 text-gray-800',
};

const statusLabels: Record<string, string> = {
  pending_review: 'Pending Review',
  approved: 'Approved',
  denied: 'Denied',
  replacement_shipped: 'Replacement Shipped',
  resolved: 'Resolved',
};

const componentLabels: Record<string, string> = {
  motor: 'Motor',
  fabric: 'Fabric',
  track: 'Track',
  housing: 'Housing',
  remote: 'Remote',
  other: 'Other',
};

const issueLabels: Record<string, string> = {
  manufacturing_defect: 'Manufacturing Defect',
  shipping_damage: 'Shipping Damage',
  motor_failure: 'Motor Failure',
  fabric_tear: 'Fabric Tear',
  track_binding: 'Track Binding',
  weather_damage: 'Weather Damage',
  other: 'Other',
};

export default function RmaHistoryPage() {
  const [rmas, setRmas] = useState<RmaRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Selected RMA for detail view
  const [selectedRma, setSelectedRma] = useState<RmaRequest | null>(null);

  // Update RMA status
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    fetchRmas();
  }, [page, searchQuery, statusFilter]);

  const fetchRmas = async () => {
    setLoading(true);
    setError(null);

    try {
      const params: any = { page, pageSize: 20 };
      if (searchQuery) params.search = searchQuery;
      if (statusFilter !== 'all') params.status = statusFilter;

      const response = await warrantyApi.listRmas(params);
      setRmas(response.data || []);
      setTotalPages(response.totalPages || 1);
      setTotal(response.total || 0);
    } catch (err: any) {
      setError(err.message || 'Failed to load RMAs');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (id: string, newStatus: string) => {
    setActionLoading(id);
    try {
      await warrantyApi.updateRma(id, { status: newStatus });
      fetchRmas();
      if (selectedRma?.id === id) {
        setSelectedRma({ ...selectedRma, status: newStatus });
      }
    } catch (err: any) {
      alert(err.message || 'Failed to update status');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">RMA History</h1>
        <p className="text-gray-500">View all processed RMA requests</p>
      </div>

      {/* Search & Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by RMA number, order number, dealer..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Statuses</option>
            <option value="approved">Approved</option>
            <option value="denied">Denied</option>
            <option value="replacement_shipped">Replacement Shipped</option>
            <option value="resolved">Resolved</option>
          </select>
        </div>
      </div>

      {/* Results count */}
      {!loading && (
        <div className="text-sm text-gray-500">
          Showing {rmas.length} of {total} RMA requests
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-500">Loading RMA history...</p>
        </div>
      ) : rmas.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-500">No RMA requests found</p>
        </div>
      ) : (
        <>
          {/* Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      RMA #
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Order
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Dealer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Component
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Issue
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {rmas.map((rma) => (
                    <tr key={rma.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-blue-600 font-medium">{rma.rmaNumber}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                        {rma.orderNumber || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                        {rma.dealerCompanyName || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                        {componentLabels[rma.component]}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                        {issueLabels[rma.issueType]}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[rma.status]}`}
                        >
                          {statusLabels[rma.status]}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-500 text-sm">
                        {formatDate(rma.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => setSelectedRma(rma)}
                          className="text-blue-600 hover:text-blue-800 font-medium"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 border border-gray-300 rounded text-sm text-gray-700 disabled:opacity-50 hover:bg-gray-100"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-500">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1 border border-gray-300 rounded text-sm text-gray-700 disabled:opacity-50 hover:bg-gray-100"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Detail Modal */}
      {selectedRma && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">RMA Details</h2>
                  <p className="text-blue-600 font-medium">{selectedRma.rmaNumber}</p>
                </div>
                <button
                  onClick={() => setSelectedRma(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-6">
                {/* Status Banner */}
                <div
                  className={`p-4 rounded-lg ${
                    selectedRma.status === 'approved' || selectedRma.status === 'replacement_shipped'
                      ? 'bg-green-50 border border-green-200'
                      : selectedRma.status === 'denied'
                      ? 'bg-red-50 border border-red-200'
                      : 'bg-gray-50 border border-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-1 text-sm font-medium rounded-full ${statusColors[selectedRma.status]}`}>
                      {statusLabels[selectedRma.status]}
                    </span>
                    {selectedRma.resolvedAt && (
                      <span className="text-gray-500 text-sm">
                        Resolved on {formatDate(selectedRma.resolvedAt)}
                      </span>
                    )}
                  </div>
                  {selectedRma.denialReason && (
                    <p className="text-red-700 mt-2">
                      <strong>Denial Reason:</strong> {selectedRma.denialReason}
                    </p>
                  )}
                  {selectedRma.adminNotes && (
                    <p className="text-gray-700 mt-2">
                      <strong>Admin Notes:</strong> {selectedRma.adminNotes}
                    </p>
                  )}
                </div>

                {/* Request Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-gray-500 text-sm">Order Number</span>
                    <p className="font-medium">{selectedRma.orderNumber || '-'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-sm">Dealer</span>
                    <p className="font-medium">{selectedRma.dealerCompanyName || '-'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-sm">Component</span>
                    <p className="font-medium">{componentLabels[selectedRma.component]}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-sm">Issue Type</span>
                    <p className="font-medium">{issueLabels[selectedRma.issueType]}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-sm">Submitted</span>
                    <p className="font-medium">{formatDate(selectedRma.createdAt)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-sm">Customer</span>
                    <p className="font-medium">{selectedRma.warrantyCustomerName || '-'}</p>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <span className="text-gray-500 text-sm">Description</span>
                  <p className="text-gray-700 bg-gray-50 p-3 rounded-lg mt-1">
                    {selectedRma.description}
                  </p>
                </div>

                {/* Photos */}
                {selectedRma.photoUrls && selectedRma.photoUrls.length > 0 && (
                  <div>
                    <span className="text-gray-500 text-sm">Photos</span>
                    <div className="grid grid-cols-3 gap-2 mt-1">
                      {selectedRma.photoUrls.map((url, index) => (
                        <a key={index} href={url} target="_blank" rel="noopener noreferrer">
                          <img
                            src={url}
                            alt={`Issue ${index + 1}`}
                            className="w-full h-28 object-cover rounded-lg hover:opacity-80"
                          />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Status Update Actions */}
                {selectedRma.status === 'approved' && (
                  <div>
                    <span className="text-gray-500 text-sm">Update Status</span>
                    <div className="flex gap-3 mt-2">
                      <button
                        onClick={() => handleUpdateStatus(selectedRma.id, 'replacement_shipped')}
                        disabled={actionLoading === selectedRma.id}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50"
                      >
                        {actionLoading === selectedRma.id ? 'Updating...' : 'Mark as Replacement Shipped'}
                      </button>
                    </div>
                  </div>
                )}

                {selectedRma.status === 'replacement_shipped' && (
                  <div>
                    <span className="text-gray-500 text-sm">Update Status</span>
                    <div className="flex gap-3 mt-2">
                      <button
                        onClick={() => handleUpdateStatus(selectedRma.id, 'resolved')}
                        disabled={actionLoading === selectedRma.id}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50"
                      >
                        {actionLoading === selectedRma.id ? 'Updating...' : 'Mark as Resolved'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setSelectedRma(null)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
